/* Cliente da API: um lugar so para o envelope de erro, o CSRF e o 401. */

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string,
              public endpoint: string) {
    super(message);
  }
}

/** Cookie legivel por JS, de proposito: o SPA devolve o valor num header, e um
 *  POST cross-site — que nao consegue ler cookie — nao tem como forjar. */
function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : "";
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, ...rest } = init;
  const headers = new Headers(rest.headers);
  if (json !== undefined) {
    headers.set("Content-Type", "application/json");
    rest.body = JSON.stringify(json);
    rest.method = rest.method ?? "POST";
  }
  if (rest.method && !["GET", "HEAD"].includes(rest.method)) {
    headers.set("X-CSRF-Token", csrfToken());
  }

  const response = await fetch(path, { ...rest, headers, credentials: "same-origin" });

  if (response.status === 401 && !path.includes("/auth/")) {
    onUnauthorized?.();
  }
  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get("content-type")?.includes("application/json");
  const body = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const detail = typeof body === "object" && body
      ? (body as { detail?: string }).detail ?? "Erro inesperado."
      : String(body).slice(0, 200);
    const code = typeof body === "object" && body
      ? (body as { code?: string }).code ?? `HTTP_${response.status}`
      : `HTTP_${response.status}`;
    throw new ApiError(response.status, code, detail, path);
  }
  return body as T;
}

export const qs = (params: Record<string, string | number | boolean | null | undefined>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== "") {
      search.set(key, String(value));
    }
  }
  const text = search.toString();
  return text ? `?${text}` : "";
};

// -- tipos que a interface consome ----------------------------------------

export interface UserRow {
  user_id: string; short_id: string; months: number; categories: number;
  total: number; cut_index: number; first_month: string; last_month: string;
  is_aggregate: boolean;
}

export interface SeriesRow {
  category: string; flow: string; values: number[]; total: number;
  realized_total: number; active_months: number; peak: number;
  last_realized_index: number | null;
}

export interface SeriesPayload {
  user_id: string; labels: string[]; cut_index: number;
  months: number; total: number; series: SeriesRow[];
}

export interface Tile {
  key: string; label: string; value: number | null; unit: string;
  flow?: string; detail?: string;
}

export interface DistributionRow {
  key: string; flow: string; n: number; zeros: number; total_users: number;
  min: number; max: number; p25: number; median: number; p75: number; sum: number;
  /** Média entre quem tem a categoria; `mean_all` divide por todos os usuários. */
  mean: number; mean_all: number;
  points: { user_id: string; value: number }[];
}

export interface MatrixPayload {
  window: number;
  columns: { key: string; flow: string }[];
  rows: { user_id: string; short_id: string; values: number[]; total: number;
          window_from?: string; window_to?: string; months?: number }[];
  column_max: number[];
  column_mean: number[];
  column_mean_active: number[];
  column_median: number[];
  column_users: number[];
  users: number;
  total_mean: number;
}

export interface EntryRow {
  id: number; tx_id: string; user_id: string; date: string; month: string;
  description: string | null; amount: number; is_chargeback: boolean; realized: boolean;
  installment_current: number | null; installment_total: number | null;
  entities: string | null; no_ontology?: boolean; short_id?: string;
  category?: string; flow?: string;
  flag?: null | { rule: string; evidence?: string; suggested_category: string | null;
                  share_pct?: number | null; rows?: number | null; severity: string };
}

export interface EntriesPayload extends Paged<EntryRow> {
  totals: { sum: number; rows: number; months?: number;
            first?: string | null; last?: string | null };
  category?: string; month?: string | null;
}

export interface DescriptionRow {
  id: number; description: string; category: string; flow: string; amount: number;
  is_income: boolean; entities: string; entity_count: number; no_ontology: boolean;
  recurring: boolean | null; installments: number | null;
  flag: null | {
    rule: string; evidence: string; suggested_category: string | null;
    share_pct: number | null; rows: number | null; severity: string;
  };
  reviewed: boolean;
}

export interface Paged<T> {
  total: number; page: number; page_size: number; pages: number; items: T[];
}

export interface GraphNode {
  /** Único no grafo inteiro: `e<id>` para entidade, `c<categoria>` para categoria. */
  key: string;
  kind: "entidade" | "categoria";
  id: number | null; type_key: string; label: string; name: string;
  rows: number; amount: number; top_category: string | null; purity: number | null;
  flow?: string;
}

export interface GraphPayload {
  nodes: GraphNode[];
  edges: { source: string; target: string; weight: number; rows: number;
           kind: "entidade" | "categoria" }[];
  total_nodes: number; shown_nodes: number; category_nodes: number; truncated: boolean;
}

export interface CategoryRow {
  category: string; flow: string; rows: number; users: number; amount: number;
  first_month: string | null; last_month: string | null;
}

export interface CategoryStudy {
  category: string; flow: string; cut_ym: string;
  users: { selected: string[]; all: boolean; with_category: number; total: number };
  totals: {
    amount: number; rows: number; months: number; users_with_category: number;
    monthly_mean: number; monthly_median: number; ticket: number;
    user_mean: number; user_median: number; share_of_flow: number | null;
  };
  monthly: { month: string; value: number; rows: number; users: number;
             realized: boolean }[];
  by_user: { user_id: string; short_id: string; total: number; rows: number;
             months: number; monthly_mean: number; share_of_flow: number | null }[];
  matrix: { months: string[];
            rows: { user_id: string; short_id: string; values: number[];
                    total: number }[] };
  entities: { type_key: string; label: string; entities: number; amount: number;
              items: { name: string; rows: number; amount: number;
                       share: number | null }[] }[];
  top_descriptions: { id: number; user_id: string; short_id: string; description: string;
                      amount: number; entities: string | null; no_ontology: boolean;
                      flag: null | { rule: string; severity: string;
                                     suggested_category: string | null } }[];
  histogram: { from: number; to: number | null; rows: number; amount: number }[];
  traits: { descriptions: number; recurring: number; recurring_answered: number;
            recurring_pct: number | null; installments: number };
}

export interface Meta {
  version: string; git_sha: string; built_at: string;
  generated_at: string; generator_version: string; cut_ym: string; source: string;
  invariants_ran: boolean; locale: string; currency: string; drift: boolean;
  counts: { users: number; categories: number; descriptions: number;
            entities: number; flagged: number };
}

export interface PurityRow {
  type_key: string; label: string; entities: number;
  median_purity: number | null; median_categories: number | null; band: string;
}

export interface AgentReport {
  user_report: string; created_at: string;
}

/* Metadados do cache de 12 h do servidor (app/agents_cache.py), injetados nas
 * respostas dos três endpoints dos agentes. `stale` só aparece quando o Supabase
 * falhou e o servidor serviu a última leitura boa que tinha. */
export interface AgentsCacheMeta {
  fetched_at: string;
  cached?: boolean;
  stale?: boolean;
}

export interface AgentsRefreshPayload {
  ok: boolean;
  results: Record<string, {
    ok: boolean; fetched_at?: string; rows_scanned?: number; error?: string;
  }>;
}

export interface ReportsLatestPayload extends AgentsCacheMeta {
  agent_ids: string[];
  users: { user_id: string; agents: Record<string, AgentReport | null> }[];
  rows_scanned: number;
}

/* Margin Finder. O backend entrega `analise` verbatim -- o objeto `output` do agente,
 * ja desembrulhado -- mais os campos derivados no servidor. Anulaveis sao `| null`
 * explicito, nao opcionais: com `strict`, um `| null` que falta vira campo em branco
 * em runtime em vez de erro de compilacao.
 *
 * Quatro agentes, quatro formatos. Os papeis diferem por agente (telecom fala em
 * operadora, saude em rede), entao nao ha union `Papel` comum -- cada schema declara
 * o seu. `Confianca` e `EstadoEconomia` seguem compartilhados. */

export type Confianca = "alta" | "media" | "baixa" | "sem_evidencia";
export type EstadoEconomia = "positiva" | "negativa" | "neutra" | "indisponivel";

/* Os campos que o servidor deriva igual para os quatro agentes (app/routers/margin.py,
 * `_comum`). `economia_anual_brl` vem do agente, nunca recalculada; `economia_confere`
 * guarda a aritmetica `max(0, (atual - novo) * 12)` para que divergencia apareca. */
export interface MargemBase {
  user_id: string;
  created_at: string;
  row_id: number | null;
  versao_schema: string | null;
  status: string | null;
  resumo: string | null;
  economia_anual_brl: number | null;
  economia_mensal_brl: number | null;
  estado_economia: EstadoEconomia;
  economia_confere: boolean | null;
}

export interface MargemPayload extends AgentsCacheMeta {
  agent_id: string;
  versao_schema: string | null;
  rows_scanned: number;
  /* Linhas descartadas por nao terem a forma do agente. Alto e esperado enquanto a
   * base nao for reprocessada: em 29/08 seguros e saude gravaram sob agent_telecom. */
  rows_ignoradas: number;
}

/* --------------------------------------------- generico: agente sem schema conhecido
 *
 * O que a aba consegue mostrar de um agente que ninguem tipou. O servidor nao deriva
 * nada alem do envelope de `_comum`, e `analise` chega como veio do agente. */

export interface MargemAgente {
  agent_id: string;
  linhas: number;
  usuarios: number;
  ultima_em: string | null;
  /** A chave da secao curada, quando existe. `null` = so tem a tela generica. */
  secao: string | null;
}

export interface MargemAgentesPayload extends AgentsCacheMeta {
  agentes: MargemAgente[];
  rows_scanned: number;
}

/** O JSON de um agente que ninguem tipou. `unknown`, nunca `any`: o renderizador
 *  decide pelo tipo em runtime, e `any` deixaria um `.map()` de string compilar e
 *  quebrar so na tela. */
export type AnaliseDesconhecida = Record<string, unknown>;

/** `analise: null` e o agente que so publica `user_report` -- `agent_profile` hoje. */
export interface GenericoUser extends MargemBase {
  analise: AnaliseDesconhecida | null;
}

export interface MarginGenericoPayload extends MargemPayload {
  users: GenericoUser[];
  /** Uniao das chaves de topo vistas em todas as linhas do agente. */
  chaves: string[];
  /** Quantos conjuntos de chaves distintos apareceram. `> 1` e o sinal que substitui
   *  a guarda `_EXIGE`, que nao existe para agente desconhecido. */
  formas: number;
  com_analise: number;
}

/* ---------------------------------------------------------------- telecom v1.1 */

export type TelecomPapel = "mesma_operadora" | "outra_grande" | "alternativa";

export interface TelecomPlano {
  papel: TelecomPapel;
  plano: string;
  operadora: string;
  modalidade: string;
  id_catalogo: number;
  preco_mensal_brl: number | null;
  custo_medio_12m_brl: number | null;
  preco_pos_promo_brl: number | null;
  internet_mbps: number | null;
  movel_gb_base: number | null;
  movel_gb_anunciado: number | null;
  linhas_inclusas: number | null;
  bonus_expira: boolean;
  confianca_do_encaixe: Confianca;
  por_que: string;
  atencao: string;
  fonte_url: string | null;
}

export interface TelecomNegociacao {
  operadora: string;
  canal: string;
  quando: string;
  alavancas: string[];
  se_travarem: string;
  fonte_url: string | null;
}

export interface TelecomAuditoria {
  limitacoes: string[];
  ids_citados: number[];
  uf_derivada: string | null;
  blocos_ausentes: string[];
  blocos_recebidos: string[];
  combos_elegiveis: number | null;
  combos_no_catalogo: number | null;
  crivos_relaxados: string[];
  filtros_aplicados: string[];
  regras_de_piso_violadas: string[];
}

export interface TelecomAnalise {
  versao_schema: string;
  status: string;
  resumo: string;
  economia_total: number | null;
  base_comparavel: boolean;
  operadora_atual: string | null;
  valor_atual_brl: number | null;
  valor_melhor_plano_brl: number | null;
  melhor_plano_id_catalogo: number | null;
  melhor_plano: TelecomPlano | null;
  propostas: TelecomPlano[];
  negociacao: TelecomNegociacao | null;
  antes_de_contratar: string[];
  premissas: string[];
  auditoria: TelecomAuditoria;
}

export interface TelecomUser extends MargemBase {
  operadora_atual: string | null;
  base_comparavel: boolean | null;
  custo_atual_brl: number | null;
  custo_novo_brl: number | null;
  melhor_plano_id_catalogo: number | null;
  plano_escolhido_nome: string | null;
  plano_escolhido_operadora: string | null;
  plano_escolhido_papel: TelecomPapel | null;
  propostas_n: number;
  analise: TelecomAnalise;
}

export interface MarginTelecomPayload extends MargemPayload {
  users: TelecomUser[];
}

/* ---------------------------------------------------------------- seguros v1.0 */

export type SeguroAvaliacao =
  "verificar" | "manter" | "substituir" | "cancelar" | "identificar";

export interface SeguroAtual {
  ramo: string;
  seguradora: string;
  descritor: string;
  custo_mensal_brl: number | null;
  custo_anual_brl: number | null;
  base_anual: string;
  avaliacao: SeguroAvaliacao;
  alerta: string | null;
  cobre_ramo: string | null;
  observacao: string;
}

export interface CarteiraItem {
  ramo: string;
  ordem: number;
  origem: "catalogo" | "manter_atual";
  produto: string | null;
  seguradora: string;
  id_seguro: string | null;
  id_catalogo: number | null;
  custo_anual_brl: number | null;
  capital_minimo_brl: number | null;
  base_do_preco: string;
  confianca: Confianca;
  coberturas_a_pedir: string[];
  por_que: string;
  atencao: string;
}

export interface ForaDaCarteira {
  ramo: string;
  motivo: string;
  custo_anual_estimado_brl: number | null;
  atencao: string;
}

export interface SegurosAuditoria {
  limitacoes: string[];
  ids_citados: string[];
  base_do_teto: string | null;
  teto_anual_brl: number | null;
  blocos_ausentes: string[];
  blocos_recebidos: string[];
  linhas_no_catalogo: number | null;
  linhas_proponiveis: number | null;
  regras_de_piso_violadas: string[];
}

export interface SegurosAnalise {
  versao_schema: string;
  status: string;
  resumo: string;
  economia_total: number | null;
  custo_mensal_atual_brl: number | null;
  custo_anual_atual_brl: number | null;
  custo_mensal_carteira_brl: number | null;
  custo_anual_carteira_brl: number | null;
  seguradora_principal_atual: string | null;
  seguros_atuais: SeguroAtual[];
  carteira_alvo: CarteiraItem[];
  fora_da_carteira: ForaDaCarteira[];
  como_obter: string[];
  antes_de_contratar: string[];
  premissas: string[];
  auditoria: SegurosAuditoria;
}

export interface SegurosUser extends MargemBase {
  seguradora_principal_atual: string | null;
  custo_mensal_atual_brl: number | null;
  custo_anual_atual_brl: number | null;
  custo_mensal_carteira_brl: number | null;
  custo_anual_carteira_brl: number | null;
  base_do_teto: string | null;
  teto_anual_brl: number | null;
  seguros_atuais_n: number;
  carteira_alvo_n: number;
  fora_da_carteira_n: number;
  analise: SegurosAnalise;
}

export interface MarginSegurosPayload extends MargemPayload {
  users: SegurosUser[];
}

/* ------------------------------------------------------------------ saude v1.0 */

export type SaudePapel = "mais_barato" | "equilibrado" | "melhor_rede";

export interface SaudePlano {
  papel: SaudePapel;
  plano: string;
  id_plano: string;
  operadora: string;
  descricao: string;
  id_catalogo: number;
  abrangencia: string;
  segmentacao: string;
  acomodacao: string;
  padrao_rede: string;
  coparticipacao: string;
  reembolso: string;
  tipo_contratacao: string;
  vidas_cobertas: number | null;
  idss: number | null;
  igr: number | null;
  custo_familia_mensal_brl: number | null;
  preco_titular_mensal_brl: number | null;
  preco_dependente_mensal_brl: number | null;
  preco_estimado: boolean;
  preco_origem: string;
  crivos_violados: string[];
  custo_de_encaixe: number | null;
  confianca_do_encaixe: Confianca;
  por_que: string;
  atencao: string;
  fonte_url: string | null;
}

export interface PlanoAtual {
  tipo: string;
  tipo_provavel: string | null;
  operadora: string;
  custo_mensal_estimado_brl: number | null;
  ultimo_mes: string | null;
  confianca_identificacao: string;
  alerta_qualidade: string | null;
  observacao: string;
}

export interface SaudeAuditoria {
  praca: string | null;
  limitacoes: string[];
  ids_citados: number[];
  blocos_ausentes: string[];
  blocos_recebidos: string[];
  planos_na_praca: number | null;
  planos_no_catalogo: number | null;
  planos_elegiveis: number | null;
  crivos_relaxados: string[];
  filtros_aplicados: string[];
  faixa_etaria_usada: string | null;
  regras_de_piso_violadas: string[];
}

export interface SaudeAnalise {
  versao_schema: string;
  status: string;
  resumo: string;
  economia_total: number | null;
  base_comparavel: boolean;
  operadora_atual: string | null;
  valor_atual_brl: number | null;
  valor_melhor_plano_brl: number | null;
  melhor_plano_id_catalogo: number | null;
  melhor_plano: SaudePlano | null;
  propostas: SaudePlano[];
  planos_atuais: PlanoAtual[];
  antes_de_contratar: string[];
  alertas_do_contrato: string[];
  premissas: string[];
  auditoria: SaudeAuditoria;
}

export interface SaudeUser extends MargemBase {
  operadora_atual: string | null;
  base_comparavel: boolean | null;
  custo_atual_brl: number | null;
  custo_novo_brl: number | null;
  praca: string | null;
  melhor_plano_id_catalogo: number | null;
  plano_escolhido_nome: string | null;
  plano_escolhido_operadora: string | null;
  plano_escolhido_papel: SaudePapel | null;
  vidas_cobertas: number | null;
  custo_de_encaixe: number | null;
  propostas_n: number;
  planos_atuais_n: number;
  analise: SaudeAnalise;
}

export interface MarginSaudePayload extends MargemPayload {
  users: SaudeUser[];
}

/* ----------------------------------------------------------- assinaturas v4.0 */

export interface Assinatura {
  rotulo: string;
  servico: string;
  categoria: string;
  no_catalogo: boolean;
  origem_custo: string;
  custo_mensal_brl: number | null;
  plano_identificado: string | null;
}

export interface Oportunidade {
  acao: string;
  tipo: string;
  servicos: string[];
  confianca: Confianca;
  fonte_url: string | null;
  o_que_perde: string | null;
  plano_catalogo: string | null;
  economia_anual_brl: number | null;
  preco_catalogo_mensal_brl: number | null;
}

export interface AssinaturasJanela {
  ultimo_mes: string | null;
  primeiro_mes: string | null;
  mes_referencia: string | null;
  meses_na_janela: number | null;
}

export interface AssinaturasAuditoria {
  janela: AssinaturasJanela;
  limitacoes: string[];
  fora_da_lista: string[];
  linhas_catalogo: number | null;
  regras_violadas: string[];
  servicos_recebidos: number | null;
  sem_linha_no_catalogo: string[];
}

export interface AssinaturasAnalise {
  versao_schema: string;
  status: string;
  resumo: string;
  mes_referencia: string | null;
  economia_total: number | null;
  custo_mensal_atual_brl: number | null;
  custo_anual_atual_brl: number | null;
  custo_mensal_otimizado_brl: number | null;
  /* String, nao lista -- ao contrario do `como_obter` de seguros. */
  como_obter: string | null;
  assinaturas: Assinatura[];
  oportunidades: Oportunidade[];
  premissas: string[];
  auditoria: AssinaturasAuditoria;
}

export interface AssinaturasUser extends MargemBase {
  mes_referencia: string | null;
  custo_mensal_atual_brl: number | null;
  custo_anual_atual_brl: number | null;
  custo_mensal_otimizado_brl: number | null;
  meses_na_janela: number | null;
  assinaturas_n: number;
  oportunidades_n: number;
  analise: AssinaturasAnalise;
}

export interface MarginAssinaturasPayload extends MargemPayload {
  users: AssinaturasUser[];
}
